import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, LoadingSpinner, Select, Text } from '../../components/ui'
import { HorizontalTableScroll } from './HorizontalTableScroll'
import { financeiroService } from '../../services/financeiro'
import { formatMoneyBr } from '../../utils/financeiroComissoesImport'
import type {
  FinanceiroAnaliseComissaoItem,
  FinanceiroAnaliseComissaoItensFilters,
} from '../../types/financeiro'
import {
  ERROR_BANNER_CLASS,
  PAGINATION_BTN_CLASS,
  PASI_ANALISE_PAGE_SIZE,
  PASI_ANALISE_PAGE_SIZE_OPTIONS,
  formatDatePtBr,
  paginationPageClass,
} from './financeiroPelUi'

type PageSizeOption = (typeof PASI_ANALISE_PAGE_SIZE_OPTIONS)[number] | 'all'

const TH_CLASS =
  'whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300'
const TD_CLASS = 'whitespace-nowrap px-3 py-2 text-sm text-gray-900 dark:text-gray-100'
const TD_RIGHT_CLASS = `${TD_CLASS} text-right`

type AppliedFilters = Omit<FinanceiroAnaliseComissaoItensFilters, 'dia' | 'page' | 'pageSize'>

export type AnaliseComparativaComissaoModalProps = {
  dia: string
  totalComissao: number
  filters: AppliedFilters
  onClose: () => void
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message
  }
  return fallback
}

function displayText(value: string | null | undefined): string {
  const text = (value ?? '').trim()
  return text.length > 0 ? text : '—'
}

const AnaliseComparativaComissaoModal: React.FC<AnaliseComparativaComissaoModalProps> = ({
  dia,
  totalComissao,
  filters,
  onClose,
}) => {
  const [rows, setRows] = useState<FinanceiroAnaliseComissaoItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSizeOption, setPageSizeOption] = useState<PageSizeOption>(PASI_ANALISE_PAGE_SIZE)
  const requestIdRef = useRef(0)

  const effectivePageSize =
    pageSizeOption === 'all' ? Math.max(total > 0 ? total : 5000, 1) : pageSizeOption

  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize))
  const startIndex = total === 0 ? 0 : (currentPage - 1) * effectivePageSize
  const endIndex = Math.min(startIndex + effectivePageSize, total)

  const loadRows = useCallback(
    async (page: number, pageSize: number) => {
      const requestId = ++requestIdRef.current
      setLoading(true)
      setError(null)
      try {
        const result = await financeiroService.listItensComissaoDia({
          dia,
          cpfCnpj: filters.cpfCnpj,
          tipoComissao: filters.tipoComissao,
          statusRecibo: filters.statusRecibo,
          page,
          pageSize,
        })
        if (requestId !== requestIdRef.current) return
        setRows(result.rows)
        setTotal(result.total)
      } catch (err) {
        if (requestId !== requestIdRef.current) return
        console.error(err)
        setError(errorMessage(err, 'Erro ao carregar os itens da comissão.'))
        setRows([])
        setTotal(0)
      } finally {
        if (requestId === requestIdRef.current) setLoading(false)
      }
    },
    [dia, filters.cpfCnpj, filters.tipoComissao, filters.statusRecibo]
  )

  useEffect(() => {
    void loadRows(currentPage, effectivePageSize)
  }, [loadRows, currentPage, effectivePageSize])

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="analise-comissao-detalhe-titulo"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="analise-comissao-detalhe-titulo"
              className="text-gray-900 dark:text-white"
            >
              Comissão Icatu — {formatDatePtBr(dia)}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              Total do dia:{' '}
              <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {formatMoneyBr(totalComissao)}
              </span>
            </Text>
          </div>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="flex shrink-0 items-center gap-1"
          >
            <XMarkIcon className="h-4 w-4" />
            Fechar
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-gray-800">
          {error && <div className={`${ERROR_BANNER_CLASS} mx-4 mt-4 sm:mx-6`}>{error}</div>}

          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <LoadingSpinner />
              <Text size="sm" className="text-gray-500 dark:text-gray-400">
                Carregando itens da comissão...
              </Text>
            </div>
          ) : (
            <>
              <div className="min-h-0 flex-1 overflow-auto">
                <HorizontalTableScroll showNavButtons={false}>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className={TH_CLASS}>Nome do Corretor</th>
                        <th className={TH_CLASS}>CPF/CNPJ</th>
                        <th className={TH_CLASS}>Apólice</th>
                        <th className={TH_CLASS}>Fatura</th>
                        <th className={TH_CLASS}>Nº Cobrança</th>
                        <th className={TH_CLASS}>Tipo</th>
                        <th className={`${TH_CLASS} text-right`}>Valor (R$)</th>
                        <th className={TH_CLASS}>Pagamento efetivo</th>
                        <th className={TH_CLASS}>Status</th>
                        <th className={TH_CLASS}>Pasta</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                          >
                            Nenhum item de comissão para este dia com os filtros aplicados
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.id}>
                            <td className={TD_CLASS}>{displayText(row.nomeCorretor)}</td>
                            <td className={TD_CLASS}>{displayText(row.cpfCnpj)}</td>
                            <td className={TD_CLASS}>{displayText(row.apolice)}</td>
                            <td className={TD_CLASS}>{displayText(row.fatura)}</td>
                            <td className={TD_CLASS}>{displayText(row.nrCobranca)}</td>
                            <td className={TD_CLASS}>{displayText(row.tipoComissao)}</td>
                            <td className={TD_RIGHT_CLASS}>
                              <span className="tabular-nums">
                                {formatMoneyBr(row.valorComissao)}
                              </span>
                            </td>
                            <td className={TD_CLASS}>{displayText(row.dataPagamentoEfetivo)}</td>
                            <td className={TD_CLASS}>{displayText(row.statusRecibo)}</td>
                            <td className={TD_CLASS}>{displayText(row.folderName)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </HorizontalTableScroll>
              </div>

              <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300">
                      {total === 0
                        ? 'Nenhum registro'
                        : `Mostrando ${startIndex + 1} a ${endIndex} de ${total.toLocaleString('pt-BR')} item${total !== 1 ? 's' : ''}`}
                    </Text>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="whitespace-nowrap">Por página</span>
                      <Select
                        size="sm"
                        className="w-auto min-w-[5.5rem]"
                        value={String(pageSizeOption)}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const value = e.target.value
                          setPageSizeOption(
                            value === 'all' ? 'all' : (Number(value) as PageSizeOption)
                          )
                          setCurrentPage(1)
                        }}
                      >
                        {PASI_ANALISE_PAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                        <option value="all">Todos</option>
                      </Select>
                    </label>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={PAGINATION_BTN_CLASS}
                      >
                        Anterior
                      </button>
                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNumber: number
                          if (totalPages <= 5) {
                            pageNumber = i + 1
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i
                          } else {
                            pageNumber = currentPage - 2 + i
                          }
                          return (
                            <button
                              key={pageNumber}
                              type="button"
                              onClick={() => setCurrentPage(pageNumber)}
                              className={paginationPageClass(currentPage === pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          )
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={PAGINATION_BTN_CLASS}
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AnaliseComparativaComissaoModal
